import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckField } from "../checkField/CheckField";
import { keys } from "lodash";

require('./OrderField.css');

export type Props<T> = {
    id: string;
    className: string;
    labelKey: string;
    optionLabelKeys: {
        [option: string]: string;
    };
    alwaysChecked?: boolean;
    unorderableOptions?: string[];
    value: T;
    onChange(t: T): void;
};

// eslint-disable-next-line @typescript-eslint/naming-convention -- had to use function rather than const because generics break in .tsx files
export function OrderField<T extends string[]>({ id, labelKey, optionLabelKeys, unorderableOptions, alwaysChecked, className, value = [] as string[] as T, onChange }: Props<T>) {
    const { t } = useTranslation();
    const enabledKeys = value;
    const disabledKeys = keys(optionLabelKeys).filter(o => !enabledKeys.includes(o)) as T;
    const sortedKeys = enabledKeys.concat(disabledKeys) as T;
    const orderedKeys = unorderableOptions ? sortedKeys.sort((a, b) => unorderableOptions?.includes(a) ? Number.MAX_VALUE : 0) : sortedKeys;
    const [ order, setOrder ] = useState<T>(orderedKeys);

    const handleArrowClick = (key: string, index: number, isMovingUp: boolean) => {
        const otherKey = isMovingUp ? order[index - 1] : order[index + 1];
        const newOrder = order.map((o: string) => o === key ? otherKey : o === otherKey ? key : o) as T;
        const newValue = newOrder
            .filter(key => value.includes(key))
            .sort((a: string, b: string) => newOrder.indexOf(a) - newOrder.indexOf(b)) as T;
        setOrder(newOrder);
        onChange(newValue);
    };

    const handleCheckboxClick = (key: string, index: number, val: boolean) => {
        const temp = [ ...value ];
        if (val) {
            temp.splice(index, 0, key);
            onChange(temp as T);
        }
        else {
            const filteredValue = temp.filter(val => val !== key);
            onChange(filteredValue as T);
        }
    };

    return (
        <div className={ 'field order-field ' + className }>
            <label htmlFor={ id }>
                { t(labelKey) }
            </label>
            <div className="_ordered-list-container">
                <ul className="_ordered-list">
                    { order.map((key: string, index: number) => {
                        const enabled = value.indexOf(key) !== -1;
                        return (
                            <li className='_ordered-item' key={ key } value={ key }>
                                <CheckField
                                    id={ `option-${key}-enabled` }
                                    label={ t(optionLabelKeys[key]) }
                                    disabled={ alwaysChecked }
                                    input={ {
                                        checked: enabled,
                                        onChange: (e: FormEvent<HTMLInputElement>) => handleCheckboxClick(key, index, e.currentTarget.checked),
                                    } }
                                />
                                <button
                                    className="ui tiny basic button _reorder-button"
                                    type="button"
                                    onClick={ () => handleArrowClick(key, index, true) }
                                    disabled={ index === 0 || unorderableOptions?.includes(key) }
                                >
                                    <i className="caret up icon"/>
                                </button>
                                <button
                                    className="ui tiny basic button _reorder-button"
                                    type="button"
                                    onClick={ () => handleArrowClick(key, index, false) }
                                    disabled={ index === (keys(optionLabelKeys).length - (unorderableOptions ? unorderableOptions.length : 0) - 1) || unorderableOptions?.includes(key) }
                                >
                                    <i className="caret down icon"/>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}